import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Car, Calendar, Users, Shield, CheckCircle, ChevronRight, DollarSign } from 'lucide-react';
import { motion } from 'framer-motion';

interface Vehicle {
  id: string;
  name: string;
  image: string;
  price: number;
  capacity: string;
  features: string[];
}

const HireACarSection = () => {
  const [selectedVehicle, setSelectedVehicle] = useState<string | null>(null);
  
  const vehicles: Vehicle[] = [
    {
      id: 'land-cruiser',
      name: 'Toyota Land Cruiser',
      image: 'https://imgur.com/GemIpY3.jpg',
      price: 150,
      capacity: '4-7 passengers',
      features: [
        'High ground clearance',
        'Excellent off-road capability',
        'Air conditioning',
        'Roof rack'
      ]
    },
    {
      id: 'land-cruiser-70',
      name: 'Land Cruiser 70 Series',
      image: 'https://imgur.com/YABl8r4.jpg',
      price: 155,
      capacity: 'Up to 7 passengers',
      features: [
        'Powerful 4x4 capability',
        'Rugged durability',
        'Roof rack',
        'Perfect for rough terrain'
      ]
    },
    {
      id: 'hiace-safari',
      name: 'Toyota HiAce Safari Van',
      image: 'https://imgur.com/yDANaU5.jpg',
      price: 120,
      capacity: '7-9 passengers',
      features: [
        'Pop-up roof for wildlife viewing',
        '4x4 capability',
        'Spacious seating',
        'Air conditioning'
      ]
    }
  ];

  return (
    <section className="py-12 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center bg-ta-green/10 rounded-full px-3 py-1 text-sm text-ta-green mb-4"
          >
            <Car className="h-4 w-4 mr-2" />
            Hire A Car
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-2xl md:text-3xl font-bold text-gray-900 mb-4"
          >
            Premium 4x4 Vehicles for Your Safari Adventure
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-lg text-gray-600 max-w-3xl mx-auto"
          >
            Choose from our fleet of well-maintained 4x4 vehicles, perfectly equipped for Uganda's diverse terrain.
          </motion.p>
        </div>

        {/* Vehicle Selection - Updated for mobile with 2 cards per row */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-6 mb-12">
          {vehicles.map((vehicle, index) => (
            <motion.div
              key={vehicle.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
              onClick={() => setSelectedVehicle(vehicle.id === selectedVehicle ? null : vehicle.id)}
              className={`group bg-white rounded-lg sm:rounded-xl border ${
                selectedVehicle === vehicle.id 
                  ? 'border-ta-green ring-2 ring-ta-green/20' 
                  : 'border-gray-200'
              } overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer`}
            >
              <div className="relative h-28 sm:h-48">
                <img 
                  src={vehicle.image}
                  alt={vehicle.name}
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="bg-white/90 text-gray-900 text-[8px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full">
                    View Details
                  </span>
                </div>
              </div>
              
              <div className="p-2 sm:p-4">
                <h3 className="text-xs sm:text-lg font-semibold text-gray-900 mb-1 sm:mb-2 line-clamp-1">{vehicle.name}</h3>
                
                <div className="flex items-center justify-between mb-1 sm:mb-3">
                  <div className="flex items-center text-[10px] sm:text-sm text-gray-600">
                    <Users className="h-2.5 w-2.5 sm:h-4 sm:w-4 text-ta-green mr-0.5 sm:mr-1" />
                    <span className="truncate">{vehicle.capacity}</span>
                  </div>
                  <div className="flex items-center text-[10px] sm:text-sm font-medium text-ta-green">
                    <DollarSign className="h-2.5 w-2.5 sm:h-4 sm:w-4 mr-0.5 sm:mr-1" />
                    ${vehicle.price}/day
                  </div>
                </div>
                
                <div className="space-y-1 sm:space-y-2">
                  {vehicle.features.slice(0, 2).map((feature, idx) => (
                    <div key={idx} className="flex items-center text-[8px] sm:text-sm text-gray-600">
                      <CheckCircle className="h-2 w-2 sm:h-4 sm:w-4 text-ta-green mr-1 sm:mr-2 flex-shrink-0" />
                      <span className="truncate">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Features */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            <div className="flex flex-col items-center text-center">
              <div className="w-8 h-8 sm:w-12 sm:h-12 bg-ta-green/10 rounded-full flex items-center justify-center mb-2 sm:mb-3">
                <Shield className="h-4 w-4 sm:h-6 sm:w-6 text-ta-green" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-0.5 sm:mb-1 text-xs sm:text-base">Fully Insured</h3>
              <p className="text-[8px] sm:text-sm text-gray-600">Comprehensive insurance coverage</p>
            </div>
            
            <div className="flex flex-col items-center text-center">
              <div className="w-8 h-8 sm:w-12 sm:h-12 bg-ta-green/10 rounded-full flex items-center justify-center mb-2 sm:mb-3">
                <Car className="h-4 w-4 sm:h-6 sm:w-6 text-ta-green" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-0.5 sm:mb-1 text-xs sm:text-base">Well-Maintained</h3>
              <p className="text-[8px] sm:text-sm text-gray-600">Regularly serviced vehicles</p>
            </div>
            
            <div className="flex flex-col items-center text-center">
              <div className="w-8 h-8 sm:w-12 sm:h-12 bg-ta-green/10 rounded-full flex items-center justify-center mb-2 sm:mb-3">
                <Calendar className="h-4 w-4 sm:h-6 sm:w-6 text-ta-green" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-0.5 sm:mb-1 text-xs sm:text-base">Flexible Rental</h3>
              <p className="text-[8px] sm:text-sm text-gray-600">Daily, weekly, monthly options</p>
            </div>
            
            <div className="flex flex-col items-center text-center">
              <div className="w-8 h-8 sm:w-12 sm:h-12 bg-ta-green/10 rounded-full flex items-center justify-center mb-2 sm:mb-3">
                <Users className="h-4 w-4 sm:h-6 sm:w-6 text-ta-green" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-0.5 sm:mb-1 text-xs sm:text-base">24/7 Support</h3>
              <p className="text-[8px] sm:text-sm text-gray-600">Round-the-clock assistance</p>
            </div>
          </div>
          
          <div className="mt-6 sm:mt-8 text-center">
            <Link 
              to="/self-drive-safaris"
              className="inline-flex items-center px-4 py-2 sm:px-6 sm:py-3 bg-ta-green text-white text-xs sm:text-base rounded-lg hover:bg-ta-green-dark transition-colors group"
            >
              View All Vehicles & Packages
              <ChevronRight className="ml-1 sm:ml-2 h-3 w-3 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HireACarSection;